import { useMutation, useQuery } from "@tanstack/react-query";
import { apiGet, apiPost } from "@/lib/apiClient";
import type {
  AddSoftHoldCommentRequest,
  BusinessAdvisorLookupItem,
  CreateSoftHoldRequest,
  EditSoftHoldCommentRequest,
  EditSoftHoldRequest,
  ResolveSoftHoldRequest,
  SoftHoldCategoryOption,
  SoftHoldHistoryResponse,
  SoftHoldMeetingTypeOption,
  SoftHoldResponse,
  SoftHoldSubCategoryOption,
  SummitMeetingLookupItem,
  UpdatePreferredDateSelectionStatusRequest
} from "@/types/softHolds";

export const softHoldKeys = {
  byMonth: (month: number, year: number) => ["soft-holds", "month", month, year] as const,
  byId: (softHoldId: number) => ["soft-holds", "id", softHoldId] as const,
  historyById: (softHoldId: number) => ["soft-holds", "history", softHoldId] as const
};

export function useSoftHoldsByMonth(month: number, year: number) {
  return useQuery({
    queryKey: softHoldKeys.byMonth(month, year),
    queryFn: () => apiGet<SoftHoldResponse[]>(`SoftHolds/GetSoftHoldsByMonth/${month}/${year}`),
    staleTime: 30_000,
    enabled: month > 0 && year > 0
  });
}

export function useSoftHoldById(softHoldId: number, enabled = true) {
  return useQuery({
    queryKey: softHoldKeys.byId(softHoldId),
    queryFn: () => apiGet<SoftHoldResponse>(`SoftHolds/GetSoftHoldsById/${softHoldId}`),
    enabled: enabled && softHoldId > 0,
    staleTime: 30_000
  });
}

export function useSoftHoldHistoryById(softHoldId: number, enabled = true) {
  return useQuery({
    queryKey: softHoldKeys.historyById(softHoldId),
    queryFn: () =>
      apiGet<SoftHoldHistoryResponse[]>(`SoftHolds/GetSoftholdsHistoryById/${softHoldId}`),
    enabled: enabled && softHoldId > 0,
    staleTime: 15_000
  });
}

export function useUpdatePreferredDateSelectionStatus() {
  return useMutation({
    mutationFn: (body: UpdatePreferredDateSelectionStatusRequest) =>
      apiPost<UpdatePreferredDateSelectionStatusRequest,SoftHoldResponse>("SoftHolds/UpdatePreferDateSelectionStatus", body)
      
  });
}

export function useResolveSoftHold() {
  return useMutation({
    mutationFn: (body: ResolveSoftHoldRequest) =>
      apiPost<ResolveSoftHoldRequest, boolean>("SoftHolds/ResolveSoftHolds", body)
  });
}

export function useCreateSoftHold() {
  return useMutation({
    mutationFn: (body: CreateSoftHoldRequest) =>
      apiPost<CreateSoftHoldRequest, number>("SoftHolds/CreateSoftHolds", body)
  });
}

export function useEditSoftHold() {
  return useMutation({
    mutationFn: (body: EditSoftHoldRequest) =>
      apiPost<EditSoftHoldRequest, boolean>("SoftHolds/EditSoftHolds", body)
  });
}

export function useSoftHoldMeetingTypes() {
  return useQuery({
    queryKey: ["soft-holds", "meeting-types"],
    queryFn: () => apiGet<SoftHoldMeetingTypeOption[]>("SoftHolds/GetMeetingTypesList"),
    staleTime: 60_000
  });
}

export function useSoftHoldCategories(meetingTypeId: number | null) {
  return useQuery({
    queryKey: ["soft-holds", "categories", meetingTypeId],
    queryFn: () =>
      apiGet<SoftHoldCategoryOption[]>(`SoftHolds/GetCategoriesByMeetingType/${meetingTypeId}`),
    enabled: meetingTypeId !== null && meetingTypeId > 0,
    staleTime: 60_000
  });
}

export function useSoftHoldSubCategories(categoryId: number | null) {
  return useQuery({
    queryKey: ["soft-holds", "sub-categories", categoryId],
    queryFn: () =>
      apiGet<SoftHoldSubCategoryOption[]>(`SoftHolds/GetSubCategoriesByCategory/${categoryId}`),
    enabled: categoryId !== null && categoryId > 0,
    staleTime: 60_000
  });
}

export function useSummitMeetingLookup(summitMeetingId: string, enabled = true) {
  const normalizedValue = summitMeetingId.trim();

  return useQuery({
    queryKey: ["summit", "meeting-details", normalizedValue],
    queryFn: () =>
      apiGet<SummitMeetingLookupItem[]>(
        `Summit/${encodeURIComponent(normalizedValue)}/meetingDetails`
      ),
    enabled: enabled && normalizedValue.length > 0,
    staleTime: 30_000
  });
}

export function useBusinessAdvisorLookup(searchTerm: string, enabled = true) {
  const normalizedValue = searchTerm.trim();

  return useQuery({
    queryKey: ["summit", "business-advisors", normalizedValue],
    queryFn: () =>
      apiGet<BusinessAdvisorLookupItem[]>(
        `Summit/${encodeURIComponent(normalizedValue)}/businessAdvisors`
      ),
    enabled: enabled && normalizedValue.length > 0,
    staleTime: 30_000
  });
}

export function useAddSoftHoldComment() {
  return useMutation({
    mutationFn: (body: AddSoftHoldCommentRequest) =>
      apiPost<AddSoftHoldCommentRequest, boolean>("DraftCalendar/AddComment", body)
  });
}

export function useEditSoftHoldComment() {
  return useMutation({
    mutationFn: (body: EditSoftHoldCommentRequest) =>
      apiPost<EditSoftHoldCommentRequest, boolean>("DraftCalendar/EditComment", body)
  });
}

/**
 * Checks whether a Summit meeting is already linked to an active soft hold.
 * Mirrors the Blazor IsMeetingLinkedToActiveSoftHold service call.
 * Pass softHoldId = 0 when creating; pass the current record's id when editing
 * so the API can exclude it from the check.
 */
export async function checkMeetingLinkedToSoftHold(
  meetingId: string,
  softHoldId: number = 0
): Promise<boolean> {
  return apiGet<boolean>(
    `SoftHolds/CheckMeetingIsLinkedToSofHold/${encodeURIComponent(meetingId)}/${softHoldId}`
  );
}
